<template>
    <section class="flex flex-col gap-4 text-ink">
        <template v-if="sectioned">
            <div v-if="overall" class="summary-card flex flex-col gap-3">
                <component :is="sectionTag" class="body-bold">Your summary</component>
                <p class="body-small">{{ overall }}</p>
            </div>
            <!-- A failed or empty synthesis still gets a card, so the report
                 never jumps from the intro straight into Category Scores. -->
            <div v-if="sections.length === 0" class="summary-card flex flex-col gap-3">
                <component :is="sectionTag" class="body-bold">Your summary</component>
                <p class="body-small text-ink-muted italic">{{ emptyText }}</p>
            </div>
            <div v-for="section in sections" :key="section.section_code" class="summary-card flex flex-col gap-4">
                <component :is="sectionTag" class="body-bold">{{ section.section_title }}</component>
                <MarkdownText v-if="section.report_section_description?.trim()" class="section-description" :text="section.report_section_description" />
                <div v-for="list in listsFor(section)" :key="list.kind" class="flex flex-col gap-2">
                    <div class="flex items-center gap-2">
                        <SummaryBadge :kind="list.kind" />
                        <component :is="listTag" class="body-small-bold">{{ list.heading }}</component>
                    </div>
                    <p v-if="list.bullets.length === 1" class="body-small text-ink-muted leading-snug">{{ list.bullets[0].text }}</p>
                    <ul v-else class="flex flex-col gap-2 body-small text-ink-muted leading-snug">
                        <li v-for="(bullet, i) in list.bullets" :key="`${list.kind}-${i}`" class="flex gap-2">
                            <span class="dot shrink-0 mt-2"></span>
                            {{ bullet.text }}
                        </li>
                    </ul>
                </div>
                <p v-if="sectionNote(section)" class="body-small text-ink-muted italic">{{ sectionNote(section) }}</p>
            </div>
        </template>
        <template v-else>
            <div class="summary-card">
                <div class="flex items-center gap-2 mb-3">
                    <SummaryBadge kind="strengths" />
                    <component :is="sectionTag" class="body-small-bold">Strengths</component>
                </div>
                <ul class="flex flex-col gap-2 body-small text-ink-muted leading-snug">
                    <li v-for="(s, i) in strengths" :key="`s-${i}`" class="flex gap-2">
                        <span class="dot shrink-0 mt-2"></span>
                        {{ s }}
                    </li>
                    <li v-if="strengths.length === 0" class="italic">{{ emptyText }}</li>
                </ul>
            </div>
            <div class="summary-card">
                <div class="flex items-center gap-2 mb-3">
                    <SummaryBadge kind="focus_areas" />
                    <component :is="sectionTag" class="body-small-bold">Where there's room to grow</component>
                </div>
                <ul class="flex flex-col gap-2 body-small text-ink-muted leading-snug">
                    <li v-for="(g, i) in growthAreas" :key="`g-${i}`" class="flex gap-2">
                        <span class="dot shrink-0 mt-2"></span>
                        {{ g }}
                    </li>
                    <li v-if="growthAreas.length === 0" class="italic">{{ emptyText }}</li>
                </ul>
            </div>
        </template>
    </section>
</template>

<script setup lang="ts">
import SummaryBadge, { type SummaryListKind } from "~vue/AssessmentResults/SummaryBadge.vue";
import type { ExecutiveSummary, ExecutiveSummaryBullet, ExecutiveSummarySection } from "~vue/AssessmentResults/types";
import MarkdownText from "~vue/MarkdownText.vue";
import { computed } from "vue";

type HeadingLevel = 2 | 3 | 4;

interface Props {
    summary: ExecutiveSummary;
    /** Level of the section (or card) titles; list titles sit one level below. Match the surrounding outline. */
    headingLevel?: HeadingLevel;
}

const props = withDefaults(defineProps<Props>(), { headingLevel: 3 });

const sectionTag = computed(() => `h${props.headingLevel}` as "h2" | "h3" | "h4");
const listTag = computed(() => `h${props.headingLevel + 1}` as "h3" | "h4" | "h5");

interface SummaryList {
    kind: SummaryListKind;
    heading: string;
    bullets: ExecutiveSummaryBullet[];
}

const sectioned = computed(() => props.summary.version === 2);
// A failed synthesis leaves the same empty lists as a genuinely quiet result;
// the flag is what tells the reader which one they are looking at.
const emptyText = computed(() => (props.summary.failed ? "Summary unavailable right now" : "No clear patterns yet."));
const partialText = "Part of this summary couldn't be generated";
const overall = computed(() => props.summary.overall?.trim() ?? "");
const sections = computed(() => props.summary.sections ?? []);
const strengths = computed(() => props.summary.strengths ?? []);
const growthAreas = computed(() => props.summary.growth_areas ?? []);

// An empty list drops out together with its heading. `focus_areas` is what
// shows as "Watch points" (the field name predates the label). Reports from
// before the two-bucket summary carry a separate legacy `watch_points` list,
// folded in here under the same heading. A side is a list of short bullets;
// reports generated while each side was a single paragraph hold one entry,
// which renders as prose.
function listsFor(section: ExecutiveSummarySection): SummaryList[] {
    const lists: SummaryList[] = [
        { kind: "strengths", heading: "Strengths", bullets: section.strengths ?? [] },
        { kind: "focus_areas", heading: "Watch points", bullets: [...(section.focus_areas ?? []), ...(section.watch_points ?? [])] },
    ];
    return lists.filter((list) => list.bullets.length > 0);
}

// A partial report had a side dropped after generation, so a missing side is
// a gap rather than a quiet result. A failed synthesis still wins.
function sectionNote(section: ExecutiveSummarySection): string {
    const lists = listsFor(section);
    if (props.summary.partial && !props.summary.failed && lists.length < 2) {
        return partialText;
    }
    return lists.length === 0 ? emptyText.value : "";
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .summary-card {
        @apply bg-surface-raised rounded-xl border border-border-strong p-5;
    }

    .dot {
        @apply w-1.5 h-1.5 rounded-full bg-current;
    }
}

/* Admin-authored copy under the section title. It stays secondary to the
   strengths and watch points, and any heading written into it sits below the
   section title in weight and size. MarkdownText's root is the [data-marked]
   element itself, so the root is styled directly, and these rules sit outside
   @layer components because the unlayered [data-marked] rules in markdown.css
   would otherwise win. */
.section-description {
    @apply body-small text-ink-muted leading-snug gap-2;
}

.section-description :deep(h1),
.section-description :deep(h2),
.section-description :deep(h3),
.section-description :deep(h4),
.section-description :deep(h5),
.section-description :deep(h6) {
    @apply scaffold-details-bold text-ink;
}

.section-description :deep(strong) {
    @apply text-ink;
}

.section-description :deep(ul:not(.list-none)),
.section-description :deep(ol:not(.list-none)) {
    @apply gap-2 pl-5;
}
</style>
